// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner, 2026-10-22) — while the headset's zoom is on
 * (vrFocusZoom.ts), a small card above the globe says how far in it is
 * ("Zoom 14.0", and "closest here" at the ceiling) and credits the aerial
 * photos the close-up shows (CC BY 4.0 asks for the credit where they are
 * seen). It faces the viewer; a viewer inside the globe finds it just under
 * their gaze instead.
 */

import type * as THREE from 'three'

const CANVAS_W = 1024
const CANVAS_H = 192
const WIDTH_M = 0.34
const HEIGHT_M = (WIDTH_M * CANVAS_H) / CANVAS_W
/** Above the globe's top, metres. */
const GAP_M = 0.06
/** Inside the globe: this far ahead of the eyes and this far below them. */
const INSIDE_AHEAD_M = 0.7
const INSIDE_BELOW_M = 0.28
/** A globe whose top is this far over the eyes has its card before the eyes, this far up. */
const HIGHEST_ABOVE_EYES_M = 0.45
const HIGH_ABOVE_EYES_M = 0.22

export interface VrZoomBadgeText {
  /** "Zoom 14.0", with "closest here" when at the ceiling. */
  title: string
  /** The credits of what the close-up shows, one line. */
  credit: string
}

export interface VrZoomBadgeHandle {
  readonly mesh: THREE.Mesh
  /** The text, or null to hide. Repaints only on change. */
  set(text: VrZoomBadgeText | null): void
  /** Place it for this frame: above the globe toward the viewer, or before the eyes from inside. */
  place(camera: THREE.Camera, globeCentre: THREE.Vector3, worldRadius: number): void
  dispose(): void
}

export function createVrZoomBadge(THREE_: typeof THREE): VrZoomBadgeHandle {
  const canvas = document.createElement('canvas')
  canvas.width = CANVAS_W
  canvas.height = CANVAS_H
  const texture = new THREE_.CanvasTexture(canvas)
  texture.colorSpace = THREE_.SRGBColorSpace
  const material = new THREE_.MeshBasicMaterial({ map: texture, transparent: true, depthTest: false, depthWrite: false })
  const geometry = new THREE_.PlaneGeometry(WIDTH_M, HEIGHT_M)
  const mesh = new THREE_.Mesh(geometry, material)
  mesh.renderOrder = 14
  mesh.visible = false
  let shown = ''
  const cam = new THREE_.Vector3()
  const toCam = new THREE_.Vector3()
  const fwd = new THREE_.Vector3()

  function paint(text: VrZoomBadgeText): void {
    const g = canvas.getContext('2d')
    if (!g) return
    g.clearRect(0, 0, CANVAS_W, CANVAS_H)
    g.beginPath()
    if (typeof g.roundRect === 'function') g.roundRect(8, 8, CANVAS_W - 16, CANVAS_H - 16, 36)
    else g.rect(8, 8, CANVAS_W - 16, CANVAS_H - 16)
    g.fillStyle = 'rgba(13, 13, 18, 0.82)'
    g.fill()
    g.lineWidth = 3
    g.strokeStyle = 'rgba(34, 197, 94, 0.55)'
    g.stroke()
    g.textAlign = 'center'
    g.textBaseline = 'middle'
    g.fillStyle = '#e8eaf0'
    g.font = '700 60px system-ui, -apple-system, sans-serif'
    g.fillText(text.title, CANVAS_W / 2, text.credit ? 70 : CANVAS_H / 2, CANVAS_W - 64)
    if (text.credit) {
      g.fillStyle = '#a0a6b4'
      g.font = '500 30px system-ui, -apple-system, sans-serif'
      g.fillText(text.credit, CANVAS_W / 2, 136, CANVAS_W - 64)
    }
    texture.needsUpdate = true
  }

  return {
    mesh,
    set(text) {
      const key = text ? `${text.title}\n${text.credit}` : ''
      if (key === shown) return
      shown = key
      mesh.visible = !!text
      if (text) paint(text)
    },
    place(camera, globeCentre, worldRadius) {
      if (!mesh.visible) return
      camera.getWorldPosition(cam)
      toCam.copy(cam).sub(globeCentre)
      const distance = toCam.length()
      const top = globeCentre.y + worldRadius + GAP_M + HEIGHT_M / 2
      if (distance < worldRadius * 1.02 || top > cam.y + HIGHEST_ABOVE_EYES_M) {
        // Inside the globe, or its top out of sight above: before the eyes,
        // and outside nearer than the surface.
        camera.getWorldDirection(fwd)
        fwd.y = 0
        if (fwd.lengthSq() < 1e-6) fwd.set(0, 0, -1)
        fwd.normalize()
        const inside = distance < worldRadius * 1.02
        const ahead = inside ? INSIDE_AHEAD_M : Math.max(0.35, Math.min(INSIDE_AHEAD_M, distance - worldRadius - 0.05))
        mesh.position.copy(cam).addScaledVector(fwd, ahead)
        mesh.position.y += inside ? -INSIDE_BELOW_M : HIGH_ABOVE_EYES_M
      } else {
        toCam.y = 0
        if (toCam.lengthSq() < 1e-6) toCam.set(0, 0, 1)
        toCam.normalize()
        mesh.position.copy(globeCentre).addScaledVector(toCam, Math.min(worldRadius * 0.5, 0.3))
        mesh.position.y = globeCentre.y + worldRadius + GAP_M + HEIGHT_M / 2
      }
      mesh.lookAt(cam)
    },
    dispose() {
      mesh.removeFromParent()
      geometry.dispose()
      material.dispose()
      texture.dispose()
    },
  }
}
